import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

const initialForm = {
  name: "",
  tagline: "",
  description: "",
  detail: "",
  aiCoachNotes: "",
  journeyStage: "",
  priceRange: "",
  ctaUrl: "",
  ctaLabel: "",
  heroImageUrl: "",
  focusAreas: "",
  tags: "",
  coachHighlight: "",
  categoryId: null,
};

const MarketplaceProductDialog = ({
  open,
  categories = [],
  product = null,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    if (!open) return;
    if (product) {
      setForm({
        name: product.name || "",
        tagline: product.tagline || "",
        description: product.description || "",
        detail: product.detail || "",
        aiCoachNotes: product.aiCoachNotes || "",
        journeyStage: product.journeyStage || "",
        priceRange: product.priceRange || "",
        ctaUrl: product.ctaUrl || "",
        ctaLabel: product.ctaLabel || "",
        heroImageUrl: product.heroImageUrl || "",
        focusAreas: product.focusAreas || "",
        tags: product.tags || "",
        coachHighlight: product.coachHighlight || "",
        categoryId: product.categoryId || categories?.[0]?.id || null,
      });
    } else {
      setForm({
        ...initialForm,
        categoryId: categories?.[0]?.id || null,
      });
    }
  }, [open, product, categories]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = () => {
    const name = String(form.name || "").trim();
    const tagline = String(form.tagline || "").trim();
    const description = String(form.description || "").trim();
    if (!name || !tagline || !description) {
      enqueueSnackbar("Name, tagline, and description are required.", { variant: "warning" });
      return;
    }
    if (!form.categoryId && form.categoryId !== 0) {
      enqueueSnackbar("Select a category (add one on the Marketplace page if the list is empty).", { variant: "warning" });
      return;
    }
    onSubmit({
      ...form,
      name,
      tagline,
      description,
      categoryId: Number(form.categoryId),
    });
  };

  const title = product ? "Edit Marketplace Product" : "Add Marketplace Product";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          {title}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Link curated vendors to categories, explain the neuro-fitness journey, and connect AI coach prompts.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2 }}>
        <FormControl fullWidth>
          <TextField
            label="Name"
            value={form.name}
            onChange={handleChange("name")}
            size="small"
          />
        </FormControl>
        <FormControl fullWidth>
          <TextField
            label="Tagline"
            value={form.tagline}
            onChange={handleChange("tagline")}
            size="small"
          />
        </FormControl>
        <FormControl fullWidth>
          <TextField
            label="Description"
            value={form.description}
            onChange={handleChange("description")}
            multiline
            minRows={3}
            size="small"
          />
        </FormControl>
        <FormControl fullWidth>
          <TextField
            label="Long-form explanation"
            value={form.detail}
            onChange={handleChange("detail")}
            multiline
            minRows={3}
            size="small"
          />
        </FormControl>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 2 }}>
          <FormControl size="small" error={categories.length === 0}>
            <InputLabel id="marketplace-category-label">Category</InputLabel>
            <Select
              labelId="marketplace-category-label"
              value={form.categoryId === null || form.categoryId === undefined || form.categoryId === "" ? "" : form.categoryId}
              label="Category"
              onChange={handleChange("categoryId")}
              disabled={categories.length === 0}
            >
              {categories.map((category) => (
                <MenuItem key={`category-${category.id}`} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
            {categories.length === 0 ? (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                No categories loaded — create a category first, then refresh this dialog.
              </Typography>
            ) : null}
          </FormControl>
          <FormControl size="small">
            <TextField
              label="Journey Stage"
              value={form.journeyStage}
              onChange={handleChange("journeyStage")}
            />
          </FormControl>
          <FormControl size="small">
            <TextField
              label="Price Range"
              value={form.priceRange}
              onChange={handleChange("priceRange")}
            />
          </FormControl>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 2 }}>
          <FormControl size="small">
            <TextField label="CTA Label" value={form.ctaLabel} onChange={handleChange("ctaLabel")} />
          </FormControl>
          <FormControl size="small">
            <TextField label="CTA URL" value={form.ctaUrl} onChange={handleChange("ctaUrl")} />
          </FormControl>
          <FormControl size="small">
            <TextField label="Hero Image URL" value={form.heroImageUrl} onChange={handleChange("heroImageUrl")} />
          </FormControl>
        </Box>
        <FormControl fullWidth>
          <TextField
            label="Focus Areas (comma separated)"
            value={form.focusAreas}
            onChange={handleChange("focusAreas")}
            size="small"
          />
        </FormControl>
        <FormControl fullWidth>
          <TextField label="Tags (comma separated)" value={form.tags} onChange={handleChange("tags")} size="small" />
        </FormControl>
        <FormControl fullWidth>
          <TextField label="AI Coach Notes" value={form.aiCoachNotes} onChange={handleChange("aiCoachNotes")} multiline minRows={3} size="small" />
        </FormControl>
        <FormControl fullWidth>
          <TextField label="Coach Highlight" value={form.coachHighlight} onChange={handleChange("coachHighlight")} size="small" />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {product ? "Update Product" : "Add Product"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MarketplaceProductDialog;
